import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { joinTournamentByCodeUseCase } from '@/src/core/dependencies';
import { useTranslation } from '@/src/i18n';
import { JoinTournamentScreen } from '@/src/presentation/screens/JoinTournamentScreen';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { isPlanLimitError } from '@/src/core/utils/planLimitErrors';
import { logger } from '@/src/core/utils';

// Server messages from join_tournament_by_code that are expected user outcomes
const JOIN_ERROR_KEYS: Record<string, string> = {
  'Invalid invite code': 'invitations.invalidCode',
  'Tournament is no longer accepting participants': 'invitations.tournamentNotSetup',
  'You are already a participant in this tournament': 'invitations.alreadyParticipant',
  'Tournament is full': 'invitations.tournamentFull',
};

function getJoinErrorKey(message: string): string | null {
  if (JOIN_ERROR_KEYS[message]) return JOIN_ERROR_KEYS[message];
  // Capacity enforced by the check_participant_limit trigger (owner's plan or the 512 hard cap)
  if (isPlanLimitError(message) || message.startsWith('Tournament has reached the maximum allowed participants')) {
    return 'invitations.tournamentFull';
  }
  return null;
}

export default function JoinTournamentContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleJoin = async () => {
    if (!user || code.trim().length === 0) return;

    try {
      setLoading(true);
      setErrorMessage('');

      const userName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'Player';

      const tournament = await joinTournamentByCodeUseCase.execute({ code, userName });

      platformAlert(t('common.success'), t('invitations.invitationAccepted'));
      router.replace(`/tournament/${tournament.id}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('errors.failedToLoad');
      const errorKey = getJoinErrorKey(message);

      if (errorKey) {
        setErrorMessage(t(errorKey));
      } else {
        logger.error('JoinTournament', 'Failed to join tournament by code', { error });
        setErrorMessage(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <JoinTournamentScreen
      code={code}
      onCodeChange={setCode}
      onJoin={handleJoin}
      loading={loading}
      errorMessage={errorMessage}
      colors={colors}
    />
  );
}
